import { useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Plus } from "lucide-react";
import type { PMIssue, UserProfile } from "@/domains/pm/types";
import {
  DisciplineBadges,
  FittedDisciplineBadges,
  AssigneeAvatar,
} from "@/domains/pm/components/shared";
import { StatusPicker } from "@/domains/pm/components/shared/StatusPicker";
import { IssueDateCalendar } from "@/domains/pm/components/shared/IssueDateCalendar";
import { DisciplineMultiSelectList } from "@/domains/pm/components/shared/DisciplineMultiSelectList";
import { UserMultiSelectList } from "@/components/ui/user-multi-select";
import { useProjectResponsibilities } from "@/domains/contacts/useResponsibilities";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { useIssueMenuActions } from "./useIssueMenuActions";

/**
 * WHA-3446 — inline cell editors for the issues list. Clicking a row's
 * status / discipline / assignee / due-date cell opens a picker popover to
 * change that field without expanding the row. All edits go through the
 * shared `useIssueMenuActions` (single-issue array) so click-to-edit,
 * right-click, and the expanded view behave identically.
 */

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

// Shared trigger styling — a borderless, hover-highlighted hit area that
// keeps the cell's existing look. `group/cell` drives the empty-state
// dash→plus swap.
const TRIGGER_CLASS =
  "group/cell -mx-1 inline-flex max-w-full items-center gap-1 rounded px-1 py-0.5 text-left align-middle focus:outline-none focus:ring-1 focus:ring-ring";

/**
 * Where a cell sits outside the list: a board card has no column header to
 * say what an empty field is, so it names it ("+ Tags") instead of a dash.
 */
interface CellVariant {
  /** Replaces the dash shown for an empty field. */
  emptyLabel?: ReactNode;
}

/** Empty editable cell — shows a muted dash that becomes a `+` on hover. */
function EmptyAffordance() {
  return (
    <span className="inline-flex h-[16px] items-center text-[11px] text-muted-foreground">
      <span className="group-hover/cell:hidden">—</span>
      <Plus className="hidden h-3 w-3 group-hover/cell:block" />
    </span>
  );
}

export const StatusCell = observer(function StatusCell({ issue }: { issue: PMIssue }) {
  const actions = useIssueMenuActions();
  return (
    <StatusPicker
      value={issue.status}
      buildingProjectId={issue.building_project_id}
      onChange={(s) => actions.applyStatus([issue], issue.building_id, s)}
      triggerClassName={TRIGGER_CLASS}
    />
  );
});

export const DisciplineCell = observer(function DisciplineCell({
  issue,
  emptyLabel,
  fitLabels = false,
}: {
  issue: PMIssue;
  /** Spell the tags out when the column has room (the list); the board keeps
   *  the codes. The trigger then spans the cell, since that is the room. */
  fitLabels?: boolean;
} & CellVariant) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const actions = useIssueMenuActions();
  const pmStore = usePMStore();
  const selected = issue.disciplines ?? [];
  const selectedIds = selected.map((d) => d.id);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) pmStore.loadDisciplines();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(TRIGGER_CLASS, fitLabels ? "flex w-full" : "flex-wrap")}
          onClick={(e) => e.stopPropagation()}
        >
          {selected.length > 0 ? (
            fitLabels ? (
              <FittedDisciplineBadges disciplines={selected} size="sm" variant="outline" />
            ) : (
              <DisciplineBadges disciplines={selected} size="sm" variant="outline" />
            )
          ) : (
            (emptyLabel ?? <EmptyAffordance />)
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-1" align="start" onClick={(e) => e.stopPropagation()}>
        <DisciplineMultiSelectList
          disciplines={pmStore.disciplines}
          selectedIds={selectedIds}
          onChange={(ids) => actions.applyDisciplines([issue], issue.building_id, ids, selectedIds)}
          emptyMessage={t.pm.issues.noDiscipline}
        />
      </PopoverContent>
    </Popover>
  );
});

export const AssigneeCell = observer(function AssigneeCell({
  issue,
  emptyLabel,
}: { issue: PMIssue } & CellVariant) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const actions = useIssueMenuActions();
  // WHA-3507: scope candidates to users with access to this issue's project.
  const projectMembers = useProjectMembersStore();
  const assignees = issue.assignees ?? [];
  const selectedIds = assignees.map((a) => a.id);
  const projectUsers: UserProfile[] = projectMembers.get(issue.building_project_id);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) projectMembers.fetch(issue.building_project_id);
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className={TRIGGER_CLASS} onClick={(e) => e.stopPropagation()}>
          {assignees.length > 0 ? (
            <span className="flex -space-x-1.5">
              {assignees.slice(0, 3).map((u) => (
                <AssigneeAvatar key={u.id} user={u} size={22} className="ring-1 ring-background" />
              ))}
              {assignees.length > 3 && (
                <span className="ml-1 inline-flex h-[22px] items-center px-1 text-[10px] font-medium text-muted-foreground">
                  +{assignees.length - 3}
                </span>
              )}
            </span>
          ) : (
            (emptyLabel ?? <EmptyAffordance />)
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <AssigneeCellList
          issue={issue}
          users={projectUsers}
          selectedIds={selectedIds}
          onChange={(ids) => actions.applyAssignees([issue], issue.building_id, ids, selectedIds)}
        />
      </PopoverContent>
    </Popover>
  );
});

/**
 * The assignee cell's list, mounted only while its popover is open so that a
 * long issue list does not look up responsibilities for every row. People
 * responsible for the issue's disciplines (Contacts) come first.
 */
function AssigneeCellList({
  issue,
  users,
  selectedIds,
  onChange,
}: {
  issue: PMIssue;
  users: UserProfile[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { t } = useLanguage();
  const { responsibleFor } = useProjectResponsibilities(
    issue.building_id,
    issue.building_project_id
  );
  const suggested = responsibleFor(issue.disciplines?.map((d) => d.id) ?? []);
  return (
    <UserMultiSelectList
      users={users}
      selectedUserIds={selectedIds}
      onChange={onChange}
      searchPlaceholder={t.pm.issues.searchUsers}
      emptyMessage={t.pm.issues.noUsersFound}
      suggestedUserIds={suggested}
      suggestedLabel={t.pm.issues.suggestedAssignees}
      othersLabel={t.pm.issues.otherAssignees}
    />
  );
}

/**
 * Start-date cell. Mirrors DueDateCell minus the overdue treatment — overdue is
 * a due-date-and-status question that a start date plays no part in. Used by
 * the Gantt's unscheduled band, where an issue is given its dates.
 */
export const StartDateCell = observer(function StartDateCell({ issue }: { issue: PMIssue }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const actions = useIssueMenuActions();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={TRIGGER_CLASS} onClick={(e) => e.stopPropagation()}>
          {issue.start_date ? (
            <span className="text-[11px] text-muted-foreground">
              {formatDate(issue.start_date)}
            </span>
          ) : (
            <EmptyAffordance />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <IssueDateCalendar
          field="start"
          startDate={issue.start_date}
          dueDate={issue.due_date}
          onChange={(d) => {
            actions.applyStartDate([issue], issue.building_id, d);
            setOpen(false);
          }}
          clearLabel={t.pm.issues.contextMenu.noStartDate}
        />
      </PopoverContent>
    </Popover>
  );
});

export const DueDateCell = observer(function DueDateCell({
  issue,
  emptyLabel,
}: { issue: PMIssue } & CellVariant) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const actions = useIssueMenuActions();
  // WHA fix carried forward from develop: only in-progress issues show
  // their past-due date in red.
  const overdue = isIssueOverdue(issue);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={TRIGGER_CLASS} onClick={(e) => e.stopPropagation()}>
          {issue.due_date ? (
            overdue ? (
              // WHA overdue styling: the date itself sits in a red pill — the
              // pill is the marker, so no separate "Overdue" word on the row.
              <span className="inline-flex items-center rounded-full bg-[#fcebeb] px-1.5 py-0.5 text-[11px] font-medium text-[#ef4129]">
                {formatDate(issue.due_date)}
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground">
                {formatDate(issue.due_date)}
              </span>
            )
          ) : (
            (emptyLabel ?? <EmptyAffordance />)
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <IssueDateCalendar
          field="due"
          startDate={issue.start_date}
          dueDate={issue.due_date}
          onChange={(d) => {
            actions.applyDueDate([issue], issue.building_id, d);
            setOpen(false);
          }}
          clearLabel={t.pm.issues.contextMenu.noDueDate}
        />
      </PopoverContent>
    </Popover>
  );
});
